import React, { useEffect, useState } from 'react';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { Box, Card, CardContent, Typography, Grid, CircularProgress, Divider,Button } from '@mui/material';
import Sidebar from '../../components/Sidebar';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import axios from 'axios';
import { useUser } from '../../context/userContext';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

const SalesPage = () => {
  const [salesData, setSalesData] = useState(null);
  const [productData, setProductData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useUser();

  useEffect(() => {
    if (!user || !user.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const sales = [
          { month: 'Jan', amount: 120 },
          { month: 'Feb', amount: 150 },
          { month: 'Mar', amount: 170 },
          { month: 'Apr', amount: 200 },
          { month: 'May', amount: 180 },
          { month: 'Jun', amount: 220 },
        ];

        const [productResponse, categoryResponse] = await Promise.all([
          axios.get(`https://api.theonlysale.com/api/product/getByUserId/${user.id}`),
          axios.get(`https://api.theonlysale.com/api/categories/get`)
        ]);

        const products = productResponse.data;
        const allCategories = categoryResponse.data;

        const productCategories = allCategories.filter(category =>
          products.some(product => product.category_id === category.category_id)
        );

        const pieChartData = {
          labels: productCategories.map(category => category.name),
          datasets: [
            {
              label: 'Product Categories',
              data: productCategories.map(() => Math.floor(Math.random() * 100) + 1),
              backgroundColor: productCategories.map(() =>
                '#' + Math.floor(Math.random()*16777215).toString(16)
              ),
            },
          ],
        };

        setSalesData({
          labels: sales.map(item => item.month),
          datasets: [
            {
              label: 'Sales',
              data: sales.map(item => item.amount),
              fill: false,
              borderColor: '#42A5F5',
              backgroundColor: '#42A5F5',
              tension: 0.4,
            },
          ],
        });

        setProductData({
          labels: products.map(item => item.name),
          datasets: [
            {
              label: 'Revenue',
              data: products.map(item => item.price),
              backgroundColor: '#66BB6A',
            },
          ],
        });

        setCategoryData(pieChartData);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);
  const accountStatus = user?.account_status;

  if (accountStatus !== 'active') {
    return (
      <Box className="p-3 min-h-screen dark:bg-gray-900 bg-gray-100" sx={{ marginLeft: { xs: 0, md: '240px' } }}>
        <Sidebar />
        <Card className="p-4 rounded-lg shadow-md dark:bg-gray-800 dark:text-white bg-white text-gray-900">
          <CardContent>
            <Typography variant="h6" gutterBottom className="text-lg font-semibold dark:text-gray-100 text-gray-800">
              Subscription Expired
            </Typography>
            <Typography className="mt-2 dark:text-gray-400 text-gray-700">
              Your subscription has expired. To access the dashboard, please renew your subscription from your profile.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2, borderRadius: '8px' }}
              href="/profile"
              className="mt-4 bg-blue-500 hover:bg-blue-600 text-white dark:bg-blue-700 dark:hover:bg-blue-800"
            >
              Go to Profile
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }
  return (
    <>
      <Sidebar />
      <Box className="p-3 ml-0 md:ml-[240px] min-h-screen dark:bg-gray-900 bg-gray-100">
        {loading ? (
          <Box className="flex justify-center items-center h-screen">
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Sales Overview */}
            <Grid item xs={12} md={7}> 
              <Card className="p-4 rounded-lg shadow-md dark:bg-gray-800 dark:text-white bg-white text-gray-900">
                <CardContent>
                  <Typography variant="h6" gutterBottom className="text-lg font-semibold dark:text-gray-100 text-gray-800">
                    Sales Overview
                  </Typography>
                  {salesData && (
                    <Line
                      data={salesData}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: {
                            display: true,
                            position: 'top',
                            labels: {
                              color: 'grey',
                            },
                          },
                        },
                        scales: {
                          x: {
                            ticks: {
                              color: 'grey',
                            },
                          },
                          y: {
                            ticks: {
                              color: 'grey',
                            },
                          },
                        },
                      }}
                      className="mt-4"
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Category Distribution */}
            <Grid item xs={12} md={5}> 
              <Card className="p-4 rounded-lg shadow-md dark:bg-gray-800 dark:text-white bg-white text-gray-900">
                <CardContent>
                  <Typography variant="h6" className="text-lg font-semibold dark:text-gray-100 text-gray-800">
                    Category Distribution
                  </Typography>
                  {categoryData && (
                    <Pie
                      data={categoryData}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: {
                            display: true,
                            position: 'top',
                            labels: {
                              color: 'grey',
                            },
                          },
                          tooltip: {
                            callbacks: {
                              label: function(context) {
                                return `${context.label}: ${context.raw}%`;
                              }
                            }
                          }
                        }
                      }}
                      className="mt-4"
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Product Performance */}
            <Grid item xs={12}>
              <Card className="p-4 rounded-lg shadow-md dark:bg-gray-800 dark:text-white bg-white text-gray-900">
                <CardContent>
                  <Typography variant="h6" gutterBottom className="text-lg font-semibold dark:text-gray-100 text-gray-800">
                    Product Performance
                  </Typography>
                  {productData && (
                    <Bar
                      data={productData}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: {
                            display: true,
                            position: 'top',
                            labels: {
                              color: 'grey',
                            },
                          },
                        },
                        scales: {
                          x: {
                            ticks: {
                              color: 'grey',
                            },
                          },
                          y: {
                            ticks: {
                              color: 'grey',
                            },
                          },
                        },
                      }}
                      className="mt-4"
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Activities */}
            <Grid item xs={12}>
              <Card className="p-4 rounded-lg shadow-md dark:bg-gray-800 dark:text-white bg-white text-gray-900">
                <CardContent>
                  <Typography variant="h6" gutterBottom className="text-lg font-semibold dark:text-gray-100 text-gray-800"> 
                    Recent Activities
                  </Typography>
                  <Divider className="mb-2 dark:bg-gray-700" />
                  <Typography variant="body2" className="text-lg font-semibold dark:text-gray-100 text-gray-800">
                    - Product "Laptop Pro" was updated.
                  </Typography>
                  <Typography variant="body2" className="text-lg font-semibold dark:text-gray-100 text-gray-800">
                    - New product "Smartphone X" was added.
                  </Typography>
                  <Typography variant="body2" className="text-lg font-semibold dark:text-gray-100 text-gray-800">
                    - Order #12345 was completed.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Performance Summary */}
            <Grid item xs={12}>
              <Card className="p-4 rounded-lg shadow-md dark:bg-gray-800 dark:text-white bg-white text-gray-900">
                <CardContent>
                  <Typography variant="h6" gutterBottom className="text-lg font-semibold dark:text-gray-100 text-gray-800">
                    Performance Summary
                  </Typography>
                  <Divider className="mb-2 dark:bg-gray-700" />
                  <Typography variant="body2" className="text-lg font-semibold dark:text-gray-100 text-gray-800">
                    Total Sales: $1234
                  </Typography>
                  <Typography variant="body2" className="text-lg font-semibold dark:text-gray-100 text-gray-800">
                    Total Revenue: $5678
                  </Typography>
                  <Typography variant="body2" className="text-lg font-semibold dark:text-gray-100 text-gray-800">
                    Products Sold: 100
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  );
};

export default SalesPage;
